import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { PropRow, SortTable } from "~/models/Commons ";
import DecommissionStatus from "~/models/enums/DecommissionStatus.enum";
import { Entries } from "~/models/enums/Entries";
import { ContractHeader } from "~/models/scripts/ContractHeader";
import { RequesterInfo } from "~/models/types/ContractsTypes";
import { PayloadType } from "~/models/types/Payload";
import { LABELS } from "~/resources/Labels";
import {
  useDeleteContractsMutation,
  useGetContractsQuery,
  useRenewalScriptMutation,
  useTriggerRequesterMutation,
} from "~/services/contacts";
import useSessionTimeout from "~/services/session/SessionTimeOut";
import { handleDeleteEntity } from "~/Utils/helpers/DeleteEntityHelper";
import {
  attachResourcesToDecomission,
  displayEntries,
  sortScripts,
} from "~/Utils/Utils";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { SIMPLAlert } from "../../components/commons/dialog/SimplAlert";
import { SIMPLMenuHeader } from "../../components/commons/header/SIMPLMenuHeader";
import { Layout } from "../../components/commons/layout/Layout";
import { FillOutputMessageModal } from "../../components/commons/outputtemplate/FillOutputMessageModal";
import { LoadingWrapper } from "../../components/commons/spiner/SimpleLoaderWrapper";
import { SIMPLTable } from "../../components/commons/table/SimplTable";
import { Alerts } from "../messages/Alerts";

export const SIMPLContract = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteContracts] = useDeleteContractsMutation();
  const [triggerRequester, { isLoading: isTriggerLoading }] =
    useTriggerRequesterMutation();
  const [renewalScript, { isLoading: isRenewalLoading }] =
    useRenewalScriptMutation();
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [item, setItem] = useState({} as RequesterInfo);
  const [isApplyMode, setIsApplyMode] = useState<boolean>(false);
  const [savedDraftIds, setSavedDraftIds] = useState<Set<string>>(new Set());
  const [isAddModalOpenDecommission, setIsAddModalOpenDecommission] =
    useState(false);
  const [isFillOutputModalOpen, setIsFillOutputModalOpen] =
    useState<boolean>(false);
  const [rows, setRows] = useState([] as PropRow[]);
  const [size, setSize] = useState<number>(5);
  const [page, setPage] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [showPagination, setShowPagination] = useState(true);
  const [totalElements, setTotalElements] = useState<number>(0);
  const INACTIVITY_LIMIT = Number(import.meta.env.VITE_INACTIVITY_LIMIT);
  const FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN = Number(
    import.meta.env.VITE_FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN,
  );
  const { data, refetch, isLoading, isFetching, isError } = useGetContractsQuery({ page: 0, size: 999 });
  const { t } = useTranslation();
  type ResponseDetails = {
    title: string;
    message: string;
  };
  const [responseDetails, setResponseDetails] =
    useState<ResponseDetails | null>(null);
  const [decommissionModalSuccess, setDecommissionModalSuccess] =
    useState(false);
  const [decommissionModalError, setDecommissionModalError] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortTable>({
    key: "",
    direction: LABELS.ASC,
  });
  const handleSort = (key: string) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === LABELS.ASC
        ? LABELS.Desc
        : LABELS.ASC;
    setSortConfig({ key, direction });
  };

  /**
   * Initializes session timeout handling.
   *
   * @param {Object} config - Configuration for session timeout.
   * @param {number} config.inactivityLimit - Time in ms before user is considered inactive.
   * @param {number} config.forcedLogoutLimit - Time in ms before forced logout after inactivity.
   * @param {Function} config.buildLogoutUrl - Function that returns the logout URL.
   * @param {Storage} config.storage - Storage mechanism (e.g., sessionStorage).
   * */
  useSessionTimeout({
    inactivityLimit: INACTIVITY_LIMIT,
    forcedLogoutLimit: FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN,
    storage: sessionStorage,
  });

  /**
   * Initializes and sets the table rows by:
   * 1. Filtering scripts based on the active tab.
   * 2. Sorting the filtered scripts according to the sort configuration.
   * 3. Mapping sorted scripts to table row format and setting rows.
   */
  const initContracts = () => {
    // Ensure payload exists before processing
    const payload = data?.payload as PayloadType | undefined;

    if (!payload || payload.length === 0) {
      setShowPagination(false);
      return;
    }
    // Attach resources, sort scripts, and customize entries
    const contractsMergedList = attachResourcesToDecomission(payload);
    const sortedScripts = sortScripts(contractsMergedList, sortConfig);
    const customizedList = displayEntries(sortedScripts, totalElements, 999);
    // Set pagination visibility and total elements count
    const totalPages = payload?.page?.totalPages ?? 0;
    setTotal(Number(totalPages));

    if (payload?.page?.totalElements) {
      setTotalElements(payload?.page?.totalElements);
    }

    // Process rows and set them
    const scriptRows: PropRow[] = customizedList.map((contractItem: any) => ({
      row: contractItem,
    }));

    // TODO: remove mock rows — demo only
    const mockRows: PropRow[] = [
      {
        row: {
          requesterUniqueId: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
          resourceStatus: DecommissionStatus.AWAITING_PROVISIONING_INPUT,
          provisionedDateTime: '2026-09-01 10:30:00',
          provisionedDateTime_raw: '2026-09-01T10:30:00Z',
          decomissionedDateTime: null,
          decomissionedDate: 'N/A',
          decomissionedDate_raw: null,
          outputTemplateName: 'N/A',
        },
      },
      {
        row: {
          requesterUniqueId: 'b2c3d4e5-f6a7-8901-bcde-f12345678901',
          resourceStatus: DecommissionStatus.AWAITING_RENEWAL_INPUT,
          provisionedDateTime: '2026-08-15 14:00:00',
          provisionedDateTime_raw: '2026-08-15T14:00:00Z',
          decomissionedDateTime: null,
          decomissionedDate: 'N/A',
          decomissionedDate_raw: null,
          outputTemplateName: 'N/A',
        },
      },
      {
        row: {
          requesterUniqueId: 'c3d4e5f6-a7b8-9012-cdef-123456789012',
          resourceStatus: DecommissionStatus.READY_TO_SEND,
          provisionedDateTime: '2026-09-03 08:45:00',
          provisionedDateTime_raw: '2026-09-03T08:45:00Z',
          decomissionedDateTime: null,
          decomissionedDate: 'N/A',
          decomissionedDate_raw: null,
          outputTemplateName: 'N/A',
        },
      },
    ];
    // end mock rows

    setRows([...scriptRows, ...mockRows]);
    setShowPagination(true);
  };

  useEffect(() => {
    initContracts();
  }, [data, sortConfig, size, total, totalElements]);

  /**
   * @effect
   * - Runs when the 'active' dependency changes.
   * - Calls setPage(1) to reset the pagination.
   */
  useEffect(() => {
    setPage(0);
    handlePaginationSize();
  }, [size]);

  const handlePaginationSize = () => {
    if ([Entries.Ten, Entries.Twenty].includes(size)) {
      setPage(0);
    }
  };
  const handleOpenFillOutputMessage = (contractItem: RequesterInfo) => {
    if (contractItem.requesterUniqueId) {
      navigate(`/contracts/${contractItem.requesterUniqueId}/output-template`, {
        state: { contractItem },
      });
    }
  };

  const handleOpenApplyOutputMessage = (contractItem: RequesterInfo) => {
    if (contractItem.requesterUniqueId) {
      setItem(contractItem);
      setIsApplyMode(true);
      setIsFillOutputModalOpen(true);
    }
  };

  const handleSaveOutputMessage = (savedId: string = item.requesterUniqueId) => {
    // Optimistic save — persists draft state locally until real Save endpoint is available
    setSavedDraftIds((prev) => new Set(prev).add(savedId));
    setDecommissionModalError(false);
    setDecommissionModalSuccess(true);
    setResponseDetails({
      title: t('Success'),
      message: t('SaveOutputMessageSuccess'),
    });
  };

  // Pick up the saved id when returning from the Output Template page
  useEffect(() => {
    const state = location.state as { savedId?: string } | null;
    if (state?.savedId) {
      handleSaveOutputMessage(state.savedId);
      navigate('/contracts', { replace: true, state: null });
    }
  }, [location.state]);

  const handleApplyOutputMessage = async () => {
    const isRenewal =
      item.resourceStatus === DecommissionStatus.RENEWAL_CREDENTIALS;
    try {
      if (isRenewal) {
        await renewalScript({
          provisioningRequestId: 0,
          requesterUniqueId: item.requesterUniqueId,
          requesterEmail: "",
        }).unwrap();
      } else {
        await triggerRequester(item.requesterUniqueId).unwrap();
      }
      setIsFillOutputModalOpen(false);
      setSavedDraftIds((prev) => {
        const next = new Set(prev);
        next.delete(item.requesterUniqueId);
        return next;
      });
      setDecommissionModalError(false);
      setDecommissionModalSuccess(true);
      setResponseDetails({
        title: t("Success"),
        message: t("ApplyOutputMessageSuccess"),
      });
      refetch();
    } catch {
      setIsFillOutputModalOpen(false);
      setDecommissionModalError(true);
      setDecommissionModalSuccess(false);
      setResponseDetails({
        title: t("Error"),
        message: t("ApplyOutputMessageError"),
      });
    }
  };

  /* Renders "Close" and "Start" buttons for alerts */
  const renderDecommissionButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label={t("Cancel")}
          secondary
          buttonHandlerAction={() => setIsAddModalOpenDecommission(false)}
        />
      </div>
      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized
          label={t("Start")}
          secondIcon={<PlayArrowIcon fontSize="small" />}
          primary
          buttonHandlerAction={handleDecommision}
        />
      </div>
    </>
  );
  const renderAlertsHandler = () => (
    <Alerts
      showSuccess={decommissionModalSuccess}
      showError={decommissionModalError}
      title={responseDetails?.title}
      successMessage={responseDetails?.message || ""}
      errorMessage={responseDetails?.message || ""}
      onCloseSucessDialog={() => setDecommissionModalSuccess(false)}
      onCloseErrorDialog={() => setDecommissionModalError(false)}
      enabledSuccess={decommissionModalSuccess}
      enabledError={decommissionModalError}
    />
  );
  const handleConfirmDecommission = (contractItem: RequesterInfo) => {
    if (contractItem.requesterUniqueId) {
      setItem(contractItem);
      setIsAddModalOpenDecommission(true);
    } else {
      console.warn(LABELS.ScriptIdDonotExist);
    }
  };
  const handleDecommision = async () => {
    handleDeleteEntity({
      deleteFn: () => deleteContracts(item.requesterUniqueId).unwrap(),
      id: item.requesterUniqueId,
      refetchFn: refetch,
      onBeforeDelete: () => setIsAddModalOpenDecommission(false),
      onSuccess: (title, message) => {
        setResponseDetails({
          title,
          message,
        });
        setDecommissionModalError(false);
        setDecommissionModalSuccess(true);
      },
      onError: (title, message) => {
        setResponseDetails({
          title,
          message,
        });
        setDecommissionModalError(true);
        setDecommissionModalSuccess(false);
      },
      t,
    });
  };

  /*Renders decommission alert with close and start actions */
  const renderDecommisionAlert = () =>
    isAddModalOpenDecommission && (
      <SIMPLAlert
        onCancel={() => setIsAddModalOpenDecommission(false)}
        title={t("Decommission")}
        actions={renderDecommissionButtons()}
        message={""}
      >
        <p>{t("MessageDecommission")}</p>
      </SIMPLAlert>
    );

  const renderFillOutputMessageModal = () =>
    isFillOutputModalOpen && (
      <FillOutputMessageModal
        item={item}
        onCancel={() => setIsFillOutputModalOpen(false)}
        onSave={handleSaveOutputMessage}
        onConfirm={handleApplyOutputMessage}
        isApplyMode={isApplyMode}
        isLoading={isTriggerLoading || isRenewalLoading}
      />
    );
  const showFilterHandler = () => setShowFilter((prev) => !prev);

  const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSize(Number(e.target.value));
  };
  const handleResetAll = () => {
    setPage(0);
    setShowFilter(false);
  };
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const renderSIMPLTable = () => (
    <SIMPLTable
      title={t("ProvisionedResources")}
      clear={handleResetAll}
      page={page}
      size={size}
      showPagination={showPagination}
      active
      showHandler={showFilterHandler}
      filterActive={showFilter}
      tableHeaders={ContractHeader}
      handleDecommision={handleConfirmDecommission}
      rows={rows}
      onChangePage={handlePageChange}
      onChange={handleSizeChange}
      sortConfig={sortConfig}
      handleSort={handleSort}
      defaultSortKey="provisionedDateTime_raw"
      chipLabels={[]}
      showButtonDecommissionInModal
      showButtonDeleteInModal={false}
      showButtonDetailsInModal={false}
      showButtonDownloadInModal={false}
      showConfigFileInModal={false}
      showEditInModal={false}
      showCopyInModal={false}
      showButtonMoreActions={true}
      lastBorder="border-l border-white"
      isActive={true}
      showReviewOutputTemplateInModal
      handleOpenReviewOutputTemplate={handleOpenFillOutputMessage}
      savedDraftIds={savedDraftIds}
      handleOpenApplyOutputMessage={handleOpenApplyOutputMessage}
    />
  );

  return (
    <SIMPLMenuHeader>
      {renderAlertsHandler()}
      <Layout>
        {renderDecommisionAlert()}
        {renderFillOutputMessageModal()}
        <LoadingWrapper
          loadingTitle={t("LoadingContracts")}
          isLoading={isLoading || isFetching}
          isError={isError}
        >
          {renderSIMPLTable()}
        </LoadingWrapper>
      </Layout>
    </SIMPLMenuHeader>
  );
};
